import React, { useState, useRef, useEffect } from 'react';
import Icon from 'ui/icon';
import Text from 'ui/text';
import { useOnClickOutside } from 'hooks';
import colors from 'theme/colors';
import { IDropdownProps, IOption } from './types';
import DropdownTitle from './Title';
import * as Layout from './Layout';

const Dropdown: React.FC<IDropdownProps> = ({
  label,
  name,
  defaultValue,
  placeholder,
  options,
  error,
  badge,
  width,
  bg,
  required,
  onClick,
  disabled,
  cypressAttribute,
  customOptionRenderer = null,
  className,
}) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<IOption | null>(defaultValue || null);
  const toggle = () => setIsOpen(!isOpen);

  const handleClick = (option: IOption) => () => {
    const { value } = option;
    setSelectedOption(option);
    setIsOpen(false);
    onClick?.(value);
  };

  useOnClickOutside(dropdownRef, () => setIsOpen(false));

  useEffect(() => {
    if (defaultValue === null) {
      setSelectedOption(defaultValue);
    }
  }, [defaultValue]);

  return (
    <div className="rounded-sm" ref={dropdownRef} style={{ width }}>
      {label && (
        <Layout.DropdownLabel
          htmlFor={name}
          data-cy={cypressAttribute && `${cypressAttribute}-label`}
        >
          {label}
          {required && <span>*</span>}
        </Layout.DropdownLabel>
      )}
      <Layout.DropdownTitle
        onClick={toggle}
        bg={bg}
        data-cy={cypressAttribute && `${cypressAttribute}-title`}
      >
        <DropdownTitle badge={badge} value={selectedOption?.value} placeholder={placeholder} />
        {!disabled && (
          <i aria-hidden="true">
            <Icon size="tiny" icon="ChevronDownIcon" />
          </i>
        )}
      </Layout.DropdownTitle>
      {error && (
        <Text size="xs" component="span" color="red">
          {error.message}
        </Text>
      )}
      {!disabled && isOpen && (
        <div className="relative z-10">
          <ul
            className={`
            absolute left-0 top-0 w-full overflow-auto rounded-b-md
            border-2 text-white
            shadow-[0_0_10px_rgba(0,0,0,0.5)] transition-all duration-500
            ease-in-out ${className}
          `}
            style={{
              backgroundColor: bg ?? colors.blue2,
              borderColor: bg ?? colors.blue2,
            }}
            data-cy={cypressAttribute && `${cypressAttribute}-list`}
          >
            {customOptionRenderer
              ? options.map((option: IOption) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      className="w-full cursor-pointer p-0 text-left hover:bg-blue hover:opacity-90"
                      onClick={handleClick(option)}
                      data-cy={cypressAttribute && `${cypressAttribute}-list-item`}
                    >
                      {customOptionRenderer(option, Layout.ListItem, {
                        key: option.value,
                        onClick: handleClick(option),
                        'data-cy': cypressAttribute && `${cypressAttribute}-list-item`,
                      })}
                    </button>
                  </li>
                ))
              : options.map((option: IOption) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      className="w-full cursor-pointer py-2 pl-4 pr-8 text-left hover:bg-blue hover:opacity-90"
                      onClick={handleClick(option)}
                      data-cy={cypressAttribute && `${cypressAttribute}-list-item`}
                    >
                      {option.optionLabel}
                    </button>
                  </li>
                ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
